import { message } from 'antd';
import uniqBy from 'lodash/uniqBy';
import { put, select, takeLatest, delay } from 'redux-saga/effects';
import { SpaceTabDocumentType } from 'api/space/space-tab';
import { StoreActionProps } from 'data/core';
import {
  ProjectTemplateProjectPhase,
  ProjectTemplateSpace,
  ProjectTemplateSpaceTab,
  ProjectTemplateTask,
} from 'data/templates/projects';
import { logger } from 'services';
import { floatUid } from 'utils/uid';
import { MentionsDataParam } from 'vendor/ckeditor/plugins/mention/constants';
import {
  SetupProjectTemplateSpace,
  setupSpaceInProjectTemplateAction,
} from '../../actions';
import {
  hasSetupSpacesInTemplate,
  selectAllTemplateTask,
} from '../../selectors';
import {
  setupSpacesOnProjectTemplate,
  setupSpacesOnProjectTemplateComplete,
  setupSpacesOnProjectTemplateInProgress,
} from '../../slices';

type SpaceTabs = AtLeast<
  ProjectTemplateSpaceTab,
  'spaceTabId' | 'spaceTabType' | 'spaceTabTitle'
>[];

const getAllProjectPhasesSelector =
  () =>
  ({ templates }): { [key: string]: ProjectTemplateProjectPhase } =>
    templates.project.data[templates.project.activeId].projectPhases;

const scrapDocumentTemplatesFromTaskDescription = (taskDescription: string) => {
  const $parsed = new DOMParser().parseFromString(
    taskDescription,
    'text/html',
  ).documentElement;
  const spaceTabs: Partial<ProjectTemplateSpaceTab>[] = [];

  $parsed
    .querySelectorAll(`[${MentionsDataParam.Type}="DOCUMENT"]`)
    .forEach((node) => {
      const spaceId = node.getAttribute(MentionsDataParam.ObjectId);
      const spaceTabTitle = node.getAttribute('data-mention') || '';

      spaceTabs.push({
        spaceTabId: spaceId != null ? parseInt(spaceId, 10) : floatUid(),
        spaceTabType: SpaceTabDocumentType.RocketlaneDocument,
        spaceTabTitle,
        url: `/templates/${spaceId}`,
      });
    });

  return spaceTabs;
};

const findRecursiveChildNodesForDocuments = (
  spaceTabs: Partial<ProjectTemplateSpaceTab>[][],
  allTasks: { [key: string]: ProjectTemplateTask },
  tasks: ProjectTemplateTask[],
) => {
  (tasks as unknown[] as number[]).forEach((taskId) => {
    const task = allTasks[taskId];

    if (task.taskDescription) {
      spaceTabs.push(
        scrapDocumentTemplatesFromTaskDescription(task.taskDescription),
      );
    }

    if (task.subTasks) {
      findRecursiveChildNodesForDocuments(spaceTabs, allTasks, task.subTasks);
    }
  });

  return spaceTabs;
};

const spaceTabIds: string[] = [];

const extractDocumentFromPhaseTasks = (
  allTasks: { [key: string]: ProjectTemplateTask },
  phase: ProjectTemplateProjectPhase,
): AtLeast<
  ProjectTemplateSpaceTab,
  'spaceTabId' | 'spaceTabType' | 'spaceTabTitle'
>[] => {
  const { tasks } = phase;
  const spaceTabs: AtLeast<
    ProjectTemplateSpaceTab,
    'spaceTabId' | 'spaceTabType' | 'spaceTabTitle'
  >[][] = [];

  findRecursiveChildNodesForDocuments(spaceTabs, allTasks, tasks);

  const uniqSpaceTabs = uniqBy(spaceTabs.flat(), 'spaceTabId');

  const redundanctSpaceTabs: SpaceTabs = [];

  uniqSpaceTabs.forEach((spaceTab) => {
    if (!spaceTabIds.includes(spaceTab.url!)) {
      redundanctSpaceTabs.push(spaceTab);
      spaceTabIds.push(spaceTab.url!);
    }
  });

  return redundanctSpaceTabs;
};

function* setupSpacesForProjectTemplate(
  action: StoreActionProps<SetupProjectTemplateSpace>,
) {
  const {
    payload: { companyName, handleFailure },
  } = action;

  try {
    const allTasks: { [key: string]: ProjectTemplateTask } = yield select(
      selectAllTemplateTask(),
    );
    const spaceSetupState = yield select(hasSetupSpacesInTemplate());

    if (spaceSetupState === 'YET_TO_START') {
      yield put(setupSpacesOnProjectTemplateInProgress());
    }

    const _phases: {
      [key: string]: ProjectTemplateProjectPhase;
    } = yield select(getAllProjectPhasesSelector());
    const phases = Object.values(_phases);

    const privatePhases = phases.filter((phase) => phase.private);
    const sharedPhases = phases.filter((phase) => !phase.private);

    const privateSpaceTabs = privatePhases.reduce(
      (spaceTabs: SpaceTabs, phase) => {
        return [
          ...spaceTabs,
          ...extractDocumentFromPhaseTasks(allTasks, phase),
        ];
      },
      [],
    );

    const sharedSpaceTabs = sharedPhases.reduce(
      (spaceTabs: SpaceTabs, phase) => {
        return [
          ...spaceTabs,
          ...extractDocumentFromPhaseTasks(allTasks, phase),
        ];
      },
      [],
    );

    const privateSpaceId = floatUid();
    const sharedSpaceId = floatUid();

    const spaceList: { [key: string]: Partial<ProjectTemplateSpace> } = {
      [privateSpaceId]: {
        spaceId: privateSpaceId,
        spaceName: `${companyName}'s private space`,
        spaceTabs: privateSpaceTabs,
        private: true,
      },
      [sharedSpaceId]: {
        spaceId: sharedSpaceId,
        spaceName: 'Shared space',
        spaceTabs: sharedSpaceTabs,
        private: false,
      },
    };

    yield put(setupSpacesOnProjectTemplate(spaceList));
    yield delay(Object.keys(spaceList).length * 2000);

    yield put(setupSpacesOnProjectTemplateComplete());
  } catch (error) {
    logger.error('There was an error setting up spaces.', { error });

    message.error('There was an error setting up spaces. Please try later');

    if (handleFailure) {
      handleFailure();
    }
  }
}

export function* watchsetupSpacesForProjectTemplateSaga(): Generator {
  yield takeLatest(
    setupSpaceInProjectTemplateAction.type,
    setupSpacesForProjectTemplate,
  );
}
